import type { HoleRange, PostEnriched } from '@/lib/db/types';
import { getCourseComboLabel } from '@/lib/courses/group';
import { resolvePostCourseSlug } from '@/lib/scoring/resolve-course-slug';
import {
  fantasyHoleKey,
  fantasyHolesForPhysicalNine,
  physicalNineKey,
  type FantasyHoleDef,
} from '@/lib/scoring/fantasy-card';

export interface CareerRingerHole {
  holeInNine: number;
  strokes: number;
  par: number;
}

export interface CareerRingerNine {
  nine: FantasyHoleDef['nine'];
  physicalNineKey: string;
  holes: CareerRingerHole[];
  total: number;
  parTotal: number;
  playedAt: string;
  courseName?: string;
  comboLabel?: string;
}

export interface CareerRingerCampus {
  campus: 'Arlington' | 'Fairfax';
  nines: CareerRingerNine[];
  total: number;
  parTotal: number;
}

type HoleScore = { holeNum: number; strokes: number };

interface NineSegment {
  physicalNineKey: string;
  holes: Map<number, number>;
  total: number;
  playedAt: string;
  courseName?: string;
  comboLabel?: string;
}

const CAMPUS_NINES: Record<'Arlington' | 'Fairfax', string[]> = {
  Arlington: ['arl-blue', 'arl-red', 'arl-white'],
  Fairfax: ['ffx-blue', 'ffx-red', 'ffx-white'],
};

function campusFromPhysicalNine(key: string): 'Arlington' | 'Fairfax' {
  return key.startsWith('arl') ? 'Arlington' : 'Fairfax';
}

function nineLabelFromPhysicalNine(key: string): FantasyHoleDef['nine'] {
  const nine = key.split('-')[1];
  return (nine.charAt(0).toUpperCase() + nine.slice(1)) as FantasyHoleDef['nine'];
}

function segmentRanges(holes: HoleRange): Array<[number, number]> {
  if (holes === 'front-9') return [[1, 9]];
  if (holes === 'back-9') return [[10, 18]];
  return [[1, 9], [10, 18]];
}

function getMemberHoleScores(post: PostEnriched, memberId: string): HoleScore[] {
  const myGroup = post.metadata?.groupScores?.find((g) => g.id === memberId);
  if (myGroup?.scores?.length) {
    return myGroup.scores
      .filter((rh) => typeof rh.strokes === 'number' && rh.strokes > 0)
      .map((rh) => ({ holeNum: rh.hole_num, strokes: rh.strokes as number }));
  }
  if (post.author_id !== memberId) return [];
  return (post.round?.round_holes || [])
    .filter((rh) => typeof rh.strokes === 'number' && rh.strokes > 0)
    .map((rh) => ({ holeNum: rh.hole_num, strokes: rh.strokes as number }));
}

function buildSegment(
  slug: string,
  scores: HoleScore[],
  start: number,
  end: number,
  playedAt: string,
  courseName?: string,
  comboLabel?: string,
): NineSegment | null {
  const segmentScores = scores.filter((s) => s.holeNum >= start && s.holeNum <= end);
  if (segmentScores.length !== 9) return null;

  const byHoleInNine = new Map<number, number>();
  let physicalKey: string | null = null;

  for (const s of segmentScores) {
    const fantasyKey = fantasyHoleKey(slug, s.holeNum);
    if (!fantasyKey) return null;
    const nineKey = physicalNineKey(fantasyKey);
    if (!nineKey) return null;
    if (physicalKey == null) physicalKey = nineKey;
    if (physicalKey !== nineKey) return null;
    const holeInNine = Number(fantasyKey.split('-')[2]);
    if (!Number.isFinite(holeInNine) || holeInNine < 1 || holeInNine > 9) return null;
    byHoleInNine.set(holeInNine, s.strokes);
  }

  if (!physicalKey || byHoleInNine.size !== 9) return null;
  for (let i = 1; i <= 9; i += 1) {
    if (!byHoleInNine.has(i)) return null;
  }

  let total = 0;
  byHoleInNine.forEach((strokes) => { total += strokes; });

  return {
    physicalNineKey: physicalKey,
    holes: byHoleInNine,
    total,
    playedAt,
    courseName,
    comboLabel,
  };
}

function collectNineSegments(posts: PostEnriched[], memberId: string): NineSegment[] {
  const segments: NineSegment[] = [];

  posts.forEach((p) => {
    if (p.type !== 'round' || !p.round) return;
    const slug = resolvePostCourseSlug(p);
    if (!slug) return;

    const scores = getMemberHoleScores(p, memberId);
    if (!scores.length) return;

    const playedAt = p.round.played_at || p.created_at;
    const courseName = p.round.course?.name || p.metadata?.courseName;
    const comboLabel = p.round.course ? getCourseComboLabel(p.round.course) : undefined;
    const ranges = segmentRanges(p.round.holes);

    ranges.forEach(([start, end]) => {
      const segment = buildSegment(slug, scores, start, end, playedAt, courseName, comboLabel);
      if (segment) segments.push(segment);
    });
  });

  return segments;
}

function segmentToCareerRingerNine(segment: NineSegment): CareerRingerNine {
  const defs = fantasyHolesForPhysicalNine(segment.physicalNineKey);
  const holes: CareerRingerHole[] = defs.map((def) => ({
    holeInNine: def.holeInNine,
    strokes: segment.holes.get(def.holeInNine) ?? 0,
    par: def.par,
  }));
  const parTotal = defs.reduce((sum, def) => sum + def.par, 0);

  return {
    nine: nineLabelFromPhysicalNine(segment.physicalNineKey),
    physicalNineKey: segment.physicalNineKey,
    holes,
    total: segment.total,
    parTotal,
    playedAt: segment.playedAt,
    courseName: segment.courseName,
    comboLabel: segment.comboLabel,
  };
}

/** Best 2 physical nines per campus (lowest gross 9-hole totals from any single round segment). */
export function buildCareerRinger(posts: PostEnriched[], memberId: string): CareerRingerCampus[] {
  const segments = collectNineSegments(posts, memberId);
  const bestByPhysicalNine = new Map<string, NineSegment>();

  segments.forEach((segment) => {
    const existing = bestByPhysicalNine.get(segment.physicalNineKey);
    if (!existing || segment.total < existing.total) {
      bestByPhysicalNine.set(segment.physicalNineKey, segment);
    }
  });

  return (['Arlington', 'Fairfax'] as const).map((campus) => {
    const nines = CAMPUS_NINES[campus]
      .map((key) => bestByPhysicalNine.get(key))
      .filter((segment): segment is NineSegment => segment != null)
      .sort((a, b) => a.total - b.total)
      .slice(0, 2)
      .map(segmentToCareerRingerNine);

    const total = nines.reduce((sum, nine) => sum + nine.total, 0);
    const parTotal = nines.reduce((sum, nine) => sum + nine.parTotal, 0);

    return { campus, nines, total, parTotal };
  });
}
